import { FC, useEffect, useState } from 'react';
import { useRoutes } from 'react-router-dom';
import { CssBaseline, Snackbar, Alert } from '@mui/material';
import SplashScreen from './components/SplashScreen';
import useAuth from './hooks/useAuth';
import useScrollReset from './hooks/useScrollReset';
import routes from './routes';
import './css.css';
import { LogLevel, Logger } from './utils/logger';
import useSnackbar from './hooks/useSnackbar';
import { CrmUpdateMessage, SnackAlertMessage, useSignalR } from './hooks/useSignalR';
import { SFA_MESSAGE_TYPE } from './constants';
import useNotifications from './hooks/useNotifications';

const App: FC<React.PropsWithChildren<unknown>> = () => {
  const content = useRoutes(routes);
  const [connectionStatus, setConnectionStatus] = useState<string>('');
  const auth = useAuth();
  const { isSnackActive, snackMessage, snackSeverity, snackTimeout, openSnackBar, closeSnackBar } = useSnackbar();
  const { reloadTaskNotifications } = useNotifications();

  window.addEventListener('offline', () => {
    setConnectionStatus('offline');
  });

  window.addEventListener('online', () => {
    setConnectionStatus('online');
  });

  const handleClose = (reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setConnectionStatus('');
  };

  useEffect(() => {
    Logger.init(LogLevel.INFO);
  }, []);

  // Use the SignalR hook to listen for messages
  useSignalR({
    companyUserId: auth.user?.id,
    onMessageReceived: (message) => {
      console.log('SignalR ReceiveMessage:', message);

      // Handle message by type
      switch (message.messageType) {
        case SFA_MESSAGE_TYPE.CrmUpdate: {
          const crmMessage = message as CrmUpdateMessage; // Narrow down type
          console.log('CrmUpdateMessage:', crmMessage);
          reloadTaskNotifications();
          break;
        }
        case SFA_MESSAGE_TYPE.SnackAlert: {
          const snackMsg = message as SnackAlertMessage; // Narrow down type
          openSnackBar(snackMsg.message, snackMsg.severity, snackMsg.timeoutMs);
          break;
        }
        default:
          console.warn('Unhandled message type:', message.messageType);
      }
    },
  });

  useScrollReset();

  const handleSnackClose = (reason) => {
    if (reason === 'timeout' || reason === 'clickaway' || reason === 'escapeKeyDown') {
      return;
    }
    closeSnackBar();
  };

  return (
    <>
      <Snackbar open={connectionStatus == 'offline'} onClose={handleClose}>
        <Alert severity="error" elevation={6} variant="filled">
          No internet connection.
        </Alert>
      </Snackbar>
      <Snackbar open={connectionStatus == 'online'} autoHideDuration={3000} onClose={handleClose}>
        <Alert severity="success" elevation={6} variant="filled">
          Connected
        </Alert>
      </Snackbar>

      <Snackbar
        open={isSnackActive}
        sx={{ top: '100px !important' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={snackTimeout}
        onClose={handleSnackClose}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} variant="filled" sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>

      {/* RTL is not currenlty in use */}
      {/* <RTL direction={settings.direction}> */}
      <>
        <CssBaseline />
        {auth.isInitialized ? content : <SplashScreen />}
      </>
    </>
  );
};

export default App;
